/**
 * Achievement Lightbox Component
 * Enhanced lightbox with metadata display and navigation
 */

class AchievementLightbox {
    constructor() {
        this.currentIndex = 0;
        this.achievements = [];
        this.lightbox = null;
        this.createLightbox();
    }

    createLightbox() {
        // Remove old lightbox if exists
        const oldLightbox = document.getElementById('achievementLightbox');
        if (oldLightbox) {
            oldLightbox.remove();
        }

        // Create new lightbox
        this.lightbox = document.createElement('div');
        this.lightbox.id = 'achievementLightbox';
        this.lightbox.className = 'achievement-lightbox';
        this.lightbox.innerHTML = `
            <button class="lightbox-close" aria-label="Close">&times;</button>
            <button class="lightbox-prev" aria-label="Previous">&#10094;</button>
            <div class="lightbox-content">
                <div class="lightbox-image-container">
                    <img id="achievementLightboxImg" src="" alt="Achievement">
                    <div class="lightbox-loading">
                        <div class="loading-spinner"></div>
                    </div>
                </div>
                <div class="lightbox-metadata-wrapper">
                    <div class="lightbox-metadata">
                        <h3 id="achievementLightboxTitle"></h3>
                        <div class="metadata-row">
                            <span class="metadata-label">Date:</span>
                            <span id="achievementLightboxDate"></span>
                        </div>
                        <div class="metadata-row">
                            <span class="metadata-label">Category:</span>
                            <span id="achievementLightboxCategory"></span>
                        </div>
                        <div class="metadata-row" id="achievementLightboxStudentsRow" style="display: none;">
                            <span class="metadata-label">Students:</span>
                            <span id="achievementLightboxStudents"></span>
                        </div>
                        <p id="achievementLightboxDescription"></p>
                        <div class="lightbox-counter">
                            <span id="achievementLightboxCurrent">1</span> / <span id="achievementLightboxTotal">1</span>
                        </div>
                    </div>
                </div>
            </div>
            <button class="lightbox-next" aria-label="Next">&#10095;</button>
        `;

        document.body.appendChild(this.lightbox);
        this.setupEvents();
    }

    setupEvents() {
        // Close button
        const closeBtn = this.lightbox.querySelector('.lightbox-close');
        closeBtn.addEventListener('click', () => this.close());

        // Navigation buttons
        const prevBtn = this.lightbox.querySelector('.lightbox-prev');
        const nextBtn = this.lightbox.querySelector('.lightbox-next');
        
        prevBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.prev();
        });
        
        nextBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.next();
        });

        // Close on overlay click
        this.lightbox.addEventListener('click', (e) => {
            if (e.target === this.lightbox) {
                this.close();
            }
        });

        // Keyboard navigation
        document.addEventListener('keydown', (e) => {
            if (!this.lightbox.classList.contains('active')) return;

            switch(e.key) {
                case 'Escape':
                    this.close();
                    break;
                case 'ArrowLeft':
                    this.prev();
                    break;
                case 'ArrowRight':
                    this.next();
                    break;
            }
        });

        // Touch/swipe support
        let startX = 0;
        let startY = 0;
        const content = this.lightbox.querySelector('.lightbox-content');

        content.addEventListener('touchstart', (e) => {
            startX = e.touches[0].clientX;
            startY = e.touches[0].clientY;
        });

        content.addEventListener('touchend', (e) => {
            const endX = e.changedTouches[0].clientX;
            const endY = e.changedTouches[0].clientY;
            const diffX = startX - endX;
            const diffY = startY - endY;

            // Only trigger if horizontal swipe is dominant
            if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
                if (diffX > 0) {
                    this.next();
                } else {
                    this.prev();
                }
            }
        });
    }

    open(index) {
        // Get achievements from gallery
        if (window.achievementGallery) {
            this.achievements = window.achievementGallery.getAchievements();
        }

        if (this.achievements.length === 0) {
            console.error('No achievements available');
            return;
        }

        this.currentIndex = index;
        this.render();
        this.lightbox.classList.add('active');
        document.body.style.overflow = 'hidden';
    }

    close() {
        this.lightbox.classList.remove('active');
        document.body.style.overflow = '';
    }

    render() {
        const achievement = this.achievements[this.currentIndex];
        if (!achievement) return;

        const img = document.getElementById('achievementLightboxImg');
        const title = document.getElementById('achievementLightboxTitle');
        const date = document.getElementById('achievementLightboxDate');
        const category = document.getElementById('achievementLightboxCategory');
        const students = document.getElementById('achievementLightboxStudents');
        const studentsRow = document.getElementById('achievementLightboxStudentsRow');
        const description = document.getElementById('achievementLightboxDescription');
        const current = document.getElementById('achievementLightboxCurrent');
        const total = document.getElementById('achievementLightboxTotal');
        const loading = this.lightbox.querySelector('.lightbox-loading');

        // Show loading
        loading.style.display = 'flex';
        img.style.opacity = '0';

        // Load image
        img.onload = () => {
            loading.style.display = 'none';
            img.style.opacity = '1';
        };

        img.src = achievement.imageUrl;
        img.alt = achievement.title;

        // Set metadata
        title.textContent = achievement.title;
        date.textContent = this.formatDate(achievement.achievementDate);
        category.textContent = achievement.category;
        description.textContent = achievement.description;

        // Show/hide students row
        if (achievement.studentNames && achievement.studentNames.trim()) {
            students.textContent = achievement.studentNames;
            studentsRow.style.display = 'flex';
        } else {
            studentsRow.style.display = 'none';
        }

        // Update counter
        current.textContent = this.currentIndex + 1;
        total.textContent = this.achievements.length;

        // Update navigation button states
        const prevBtn = this.lightbox.querySelector('.lightbox-prev');
        const nextBtn = this.lightbox.querySelector('.lightbox-next');

        if (this.achievements.length <= 1) {
            prevBtn.style.display = 'none';
            nextBtn.style.display = 'none';
        } else {
            prevBtn.style.display = 'flex';
            nextBtn.style.display = 'flex';
        }
    }

    next() {
        this.currentIndex = (this.currentIndex + 1) % this.achievements.length;
        this.render();
    }

    prev() {
        this.currentIndex = (this.currentIndex - 1 + this.achievements.length) % this.achievements.length;
        this.render();
    }

    formatDate(dateStr) {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    }
}

// Auto-initialize
document.addEventListener('DOMContentLoaded', () => {
    window.achievementLightbox = new AchievementLightbox();
});

export { AchievementLightbox };
